import React from "react";
import { motion } from "framer-motion";
import { Users, LandPlot, TrendingUp } from "lucide-react";
import { Reveal, Overline, MaskedHeading } from "@/components/ui-bits";
import { stagger, fadeUp, viewport } from "@/lib/motion";

const ITEMS = [
  {
    icon: Users,
    n: "01",
    title: "Fake Partners",
    text: "Childhood friends, family members, and 'trusted' contacts who exploit your excitement and distance.",
  },
  {
    icon: LandPlot,
    n: "02",
    title: "Fraudulent Land",
    text: "Forged title deeds, boundary disputes, and land sold by people who don't fully own it.",
  },
  {
    icon: TrendingUp,
    n: "03",
    title: "Inflated Numbers",
    text: "Prices marked up 30–50%+ because sellers assume diaspora investors won't verify.",
  },
];

export default function Problem() {
  return (
    <section
      id="problem"
      className="relative bg-paper py-24 sm:py-32"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Overline index={1}>The Reality</Overline>
          </Reveal>
          <MaskedHeading
            tag="h2"
            className="mt-6 font-display font-bold text-navy leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
            lines={["The Best Investments in Ghana", "Are the Ones You Verify First."]}
          />
        </div>

        <motion.div
          variants={stagger(0.15)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-16 grid md:grid-cols-3 gap-px bg-navy/10 border border-navy/10"
        >
          {ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                variants={fadeUp}
                className="group bg-paper hover:bg-white transition-colors duration-500 p-8 sm:p-10"
                data-testid={`problem-card-${it.n}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 flex items-center justify-center border border-gold/40 text-gold group-hover:bg-gold group-hover:text-navy-deep transition-colors duration-500">
                    <Icon size={22} />
                  </div>
                  <span className="font-display text-4xl text-navy/10 group-hover:text-gold/40 transition-colors duration-500">
                    {it.n}
                  </span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-semibold text-navy">
                  {it.title}
                </h3>
                <p className="mt-3 text-ink/65 leading-relaxed text-[15px]">
                  {it.text}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        <Reveal delay={0.15} className="mt-14 max-w-3xl">
          <p className="text-lg sm:text-xl text-ink/80 leading-relaxed font-display">
            Every year, people lose{" "}
            <span className="text-gold font-semibold">real money</span> to
            preventable fraud — not because they were careless, but because they
            had no way to verify from a distance.
            <span className="text-navy font-semibold italic"> Until now.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
